import { ct_admins } from '@/controllers/admins';

const {
    signIn,
    getPrefectureAccounts,
    updatePrefectureStatus,
    getWhatsappInfo,
    restartWhatsapp,
    getWhatsappQrCode,
    getTenants,
    getUsers,
    grantTenantTrial,
    getTenantTrials,
    revokeTenantTrial,
    getAllCodesVerify,
    getAnalytics,
    exportAnalyticsCsv
} = ct_admins;

const state = {
    admin_logged: false,
    access_token: null,
    prefectures: [],
    pagination_prefectures: {},
    whatsapp: null,
    tenants: [],
    pagination_tenants: {},
    users_list: [],
    pagination_users: {},
    tenant_trials: [],
    pagination_tenant_trials: {}
}

const mutations = {
    SET_ADMIN_TOKEN(state, token) {
        state.admin_logged = true;
        state.access_token = token;
    },
    SET_PREFECTURES(state, payload) {
        state.prefectures = payload.data;
        state.pagination_prefectures = payload.pagination;
    },
    SET_WHATSAPP(state, payload) {
        state.whatsapp = payload;
    },
    SET_TENANTS(state, payload) {
        state.tenants = payload.data;
        state.pagination_tenants = payload.pagination;
    },
    SET_USERS_LIST(state, payload) {
        state.users_list = payload.data;
        state.pagination_users = payload.pagination;
    },
    SET_TENANT_TRIALS(state, payload) {
        state.tenant_trials = payload.data;
        state.pagination_tenant_trials = payload.pagination;
    },
    ERASE_ADMIN_INFO(state) {
        state.admin_logged = false;
        state.access_token = null;
        state.prefectures = [];
        state.pagination_prefectures = {};
        state.whatsapp = null;
        state.tenants = [];
        state.pagination_tenants = {};
        state.users_list = [];
        state.pagination_users = {};
        state.tenant_trials = [];
        state.pagination_tenant_trials = {};
    }
}

const getters = {
    get_admin_data(state) {
        return state;
    },
    get_prefectures(state) {
        return {
            data: state.prefectures,
            pagination: state.pagination_prefectures
        };
    },
    get_whatsapp(state) {
        return state.whatsapp;
    },
    get_tenants(state) {
        return {
            data: state.tenants,
            pagination: state.pagination_tenants
        };
    },
    get_users_list(state) {
        return {
            data: state.users_list,
            pagination: state.pagination_users
        };
    },
    get_tenant_trials(state) {
        return {
            data: state.tenant_trials,
            pagination: state.pagination_tenant_trials
        };
    }
}

const actions = {
    async login({ commit }, payload) {
        try {
            const { email, password } = payload;

            if (!email || !password) {
                throw "Precisa fornecer um e-mail e uma senha";
            }

            const response = await signIn({ email, password });

            if (!response.status) {
                throw response.error;
            }

            commit('SET_ADMIN_TOKEN', response.access_token);

            return response;
        } catch (error) {
            throw error;
        }
    },

    async fetchPrefectures({ commit, state }, { status = 'pending', page = 1 } = {}) {
        try {
            const token = state.access_token;

            const prefectures = await getPrefectureAccounts(token, status, page);

            if (!prefectures.status) {
                if (prefectures.isEmpty) {
                    return prefectures;
                }
                throw prefectures.error;
            }

            const list = prefectures.data?.data ?? prefectures.data ?? [];

            commit('SET_PREFECTURES', {
                data: list,
                pagination: prefectures.data?.pagination ?? {}
            });

            return prefectures;
        } catch (error) {
            throw error;
        }
    },

    async setPrefectureStatus({ dispatch, state }, { id, ...payload }) {
        try {
            const token = state.access_token;

            const response = await updatePrefectureStatus(token, id, payload);

            if (!response.status) {
                throw response.error;
            }

            await dispatch('fetchPrefectures');

            return response;
        } catch (error) {
            throw error;
        }
    },

    async fetchWhatsappInfo({ commit, state }) {
        try {
            const token = state.access_token;

            const whatsapp = await getWhatsappInfo(token);

            if (whatsapp.ignore) return;

            if (!whatsapp.status) {
                if (whatsapp.isEmpty) {
                    return whatsapp;
                }
                throw whatsapp.error;
            }

            commit('SET_WHATSAPP', whatsapp.data ?? whatsapp);

            return whatsapp;
        } catch (error) {
            throw error;
        }
    },

    async resetWhatsappSession({ dispatch, state }) {
        try {
            const token = state.access_token;

            const response = await restartWhatsapp(token);

            if (!response.status) {
                throw response.error;
            }

            await dispatch('fetchWhatsappInfo');

            return response;
        } catch (error) {
            throw error;
        }
    },

    async fetchQrCode({ state }) {
        try {
            const token = state.access_token;

            const qrCode = await getWhatsappQrCode(token);

            if (!qrCode.status) {
                throw qrCode.error;
            }

            return qrCode.url;
        } catch (error) {
            throw error;
        }
    },

    async fetchTenants({ commit, state }, { page = 1, limit = 10, search = '' } = {}) {
        try {
            const token = state.access_token;

            const tenants = await getTenants(token, { page, limit, search });

            if (!tenants.status) {
                if (tenants.isEmpty) {
                    commit('SET_TENANTS', { data: [], pagination: {} });
                    return tenants;
                }
                throw tenants.error;
            }

            commit('SET_TENANTS', {
                data: tenants.data ?? [],
                pagination: tenants.pagination ?? {}
            });

            return tenants;
        } catch (error) {
            throw error;
        }
    },

    async fetchUsers({ commit, state }, { page = 1, limit = 10, search = '', role = '' } = {}) {
        try {
            const token = state.access_token;

            const users = await getUsers(token, { page, limit, search, role });

            if (!users.status) {
                if (users.isEmpty) {
                    commit('SET_USERS_LIST', { data: [], pagination: {} });
                    return users;
                }
                throw users.error;
            }

            commit('SET_USERS_LIST', {
                data: users.data ?? [],
                pagination: users.pagination ?? {}
            });

            return users;
        } catch (error) {
            throw error;
        }
    },

    async grantTrial({ state }, { tenant_id, days, reason }) {
        try {
            const token = state.access_token;

            const response = await grantTenantTrial(token, tenant_id, { days, reason });

            if (!response.status) {
                throw response.error;
            }

            return response;
        } catch (error) {
            throw error;
        }
    },

    async fetchTenantTrials({ commit, state }, { page = 1, limit = 10, active = false } = {}) {
        try {
            const token = state.access_token;

            const trials = await getTenantTrials(token, { page, limit, active });

            if (!trials.status) {
                if (trials.isEmpty) {
                    commit('SET_TENANT_TRIALS', { data: [], pagination: {} });
                    return trials;
                }
                throw trials.error;
            }

            commit('SET_TENANT_TRIALS', {
                data: trials.data ?? [],
                pagination: trials.pagination ?? {}
            });

            return trials;
        } catch (error) {
            throw error;
        }
    },

    async revokeTrial({ state }, tenant_id) {
        try {
            const token = state.access_token;

            const response = await revokeTenantTrial(token, tenant_id);

            if (!response.status) {
                throw response.error;
            }

            return response;
        } catch (error) {
            throw error;
        }
    },

    // a análise é consulta sob demanda, sem cache: quem chama usa a resposta direto
    async fetchAnalytics({ state }, { resource, params = {} }) {
        const response = await getAnalytics(state.access_token, resource, params);

        if (!response.status) {
            throw response.error;
        }

        return response;
    },

    async exportAnalytics({ state }, { resource, params = {} }) {
        const response = await exportAnalyticsCsv(state.access_token, resource, params);

        if (!response.status) {
            throw response.error;
        }

        return response;
    },

    async fetchCodesVerify({ state }) {
        try {
            const token = state.access_token;

            const response = await getAllCodesVerify(token);

            if (!response.status) {
                throw response.error;
            }

            return response;
        } catch (error) {
            throw error;
        }
    },

    logout({ commit }) {
        commit('ERASE_ADMIN_INFO');
    }
}

export default {
    namespaced: true,
    state,
    mutations,
    getters,
    actions
};
